/**
 * units — Wave B · Turn 1 conversion + display foundation, now wired into
 * MeasureTool.jsx / useMeasurementController via UnitPreferenceContext.
 *
 * Mirrors the exact conversion factors in `backend/services/units.py`.
 * Same documented fallback policy as the backend: bare "ft"/"sf" == the
 * international foot/square foot (matches lib/sheetScale.js's existing
 * 0.3048 m/ft math). "us_survey_ft" is a distinct, explicit unit code.
 */
import { formatFeet, formatArea } from "./sheetScale";

export const M_PER_INT_FT = 0.3048;
export const M_PER_US_SURVEY_FT = 1200 / 3937;
export const M_PER_YARD = 0.9144;
export const M_PER_INCH = 0.0254;

export const LENGTH_M_PER_UNIT = {
  ft: M_PER_INT_FT,
  int_ft: M_PER_INT_FT,
  us_survey_ft: M_PER_US_SURVEY_FT,
  m: 1,
  cm: 0.01,
  in: M_PER_INCH,
  yd: M_PER_YARD,
};

export const AREA_SQM_PER_UNIT = {
  sf: M_PER_INT_FT * M_PER_INT_FT,
  sm: 1,
  sy: M_PER_YARD * M_PER_YARD,
};

export const UNIT_LABELS = {
  ft: "ft (legacy)",
  int_ft: "ft (international)",
  us_survey_ft: "ft (US survey)",
  m: "m",
  cm: "cm",
  in: "in",
  yd: "yd",
  sf: "SF (legacy)",
  sm: "m\u00b2",
  sy: "SY",
  coord: "coordinate",
};

export function isLengthUnit(unit) {
  return Object.prototype.hasOwnProperty.call(LENGTH_M_PER_UNIT, unit);
}

export function isAreaUnit(unit) {
  return Object.prototype.hasOwnProperty.call(AREA_SQM_PER_UNIT, unit);
}

export function convertLength(value, fromUnit, toUnit) {
  if (!isLengthUnit(fromUnit) || !isLengthUnit(toUnit)) {
    throw new Error(`Unknown length unit: ${fromUnit} -> ${toUnit}`);
  }
  const meters = value * LENGTH_M_PER_UNIT[fromUnit];
  return meters / LENGTH_M_PER_UNIT[toUnit];
}

export function convertArea(value, fromUnit, toUnit) {
  if (!isAreaUnit(fromUnit) || !isAreaUnit(toUnit)) {
    throw new Error(`Unknown area unit: ${fromUnit} -> ${toUnit}`);
  }
  const sqm = value * AREA_SQM_PER_UNIT[fromUnit];
  return sqm / AREA_SQM_PER_UNIT[toUnit];
}

// ---------------------------------------------------------------------------
// UI wiring — Wave B · Turn 1 real-user-gap closure. `ft` (raw, computed
// from sheet scale) is always the source of truth for length/area; these
// helpers convert + format for whatever unit the user has picked.
// ---------------------------------------------------------------------------

export const LENGTH_UNIT_CHOICES = [
  { id: "ft", label: "Feet (International)", short: "FT" },
  { id: "us_survey_ft", label: "Feet (US Survey)", short: "US-FT" },
  { id: "m", label: "Meters", short: "M" },
];

export function formatLength(rawFt, unit) {
  if (rawFt == null || !Number.isFinite(rawFt)) return "—";
  if (unit === "m") {
    const meters = convertLength(rawFt, "ft", "m");
    const abs = Math.abs(meters);
    if (abs < 1) return `${Math.round(meters * 100)} cm`;
    if (abs < 1000) return `${meters.toFixed(abs < 10 ? 1 : 0)} m`;
    return `${(meters / 1000).toFixed(2)} km`;
  }
  if (unit === "us_survey_ft") {
    const surveyFt = convertLength(rawFt, "ft", "us_survey_ft");
    const abs = Math.abs(surveyFt);
    if (abs < 1000) return `${surveyFt.toFixed(abs < 10 ? 1 : 0)} ft (US)`;
    return `${Math.round(surveyFt).toLocaleString()} ft (US)`;
  }
  // "ft" / "int_ft" — delegate to the existing feet-inches-miles formatter.
  return formatFeet(rawFt);
}

export function formatAreaValue(rawSf, unit) {
  if (rawSf == null || !Number.isFinite(rawSf)) return "—";
  if (unit === "sm") {
    const sqm = convertArea(rawSf, "sf", "sm");
    return sqm < 10000 ? `${Math.round(sqm).toLocaleString()} m\u00b2` : `${(sqm / 10000).toFixed(2)} ha`;
  }
  return formatArea(rawSf);
}

export function areaUnitForLengthUnit(lengthUnit) {
  return lengthUnit === "m" ? "sm" : "sf";
}

// ---------------------------------------------------------------------------
// Wave B · Turn 2 — historical measurement integrity. A measurement's
// stored `label` is a display string baked in at creation time. When the
// geometry-integrity backfill corrects `value` (e.g. the pre-fix aspect
// -ratio bug), the stale baked label is NEVER trustworthy again — it must
// not be shown as the headline. Every render site must call this instead
// of reading `m.label` directly for a geometry-dependent measurement.
// ---------------------------------------------------------------------------
export function measurementDisplayLabel(m) {
  if (!m) return "—";
  if (!m.geometry_recomputed_at) return m.label || null;
  const kind = m.kind;
  const unit = m.unit || "ft";
  if (m.value == null) return m.label || "—";
  if (kind === "angle") return `${Number(m.value).toFixed(1)}\u00b0`;
  if (kind === "count") return m.label || `${(m.points || []).length} items`;
  if (kind === "area") {
    const rawSf = isAreaUnit(unit) ? convertArea(m.value, unit, "sf") : m.value;
    return formatAreaValue(rawSf, isAreaUnit(unit) ? unit : "sf");
  }
  const rawFt = isLengthUnit(unit) ? convertLength(m.value, unit, "ft") : m.value;
  return formatLength(rawFt, isLengthUnit(unit) ? unit : "ft");
}
